import { DocumentType } from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedTime, IntlShape } from 'react-intl';
import { Link } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { getUrlSegment } from '@synergy/utils';

import { PoolTimelineItem } from '../../../common/components/timeline/models/pool-timeline-item';
import { TransitionActivity } from '../../../common/models/transition-activity';
import { geDocumentTypeFromString } from '../../../common/utils/get-document-type-from-string';
import { getDocumentTranslationId } from '../../../common/utils/get-document-name-translation-id';

/**
 * Maps a transition onto a timeline item.
 *
 * Extracted from the transitions panel for the document stream (#2154), which
 * mixes transitions and activities into one list and therefore maps a single
 * entry at a time.
 */

export interface TransitionTimelineContext {
    intl: IntlShape;
    /** The document the panel is shown on — it decides the reading direction. */
    sourceDocumentType: DocumentType;
    sourceDocumentId: number;
}

const getSubHeader = (transition: TransitionActivity) => (
    <span>
        {transition.createdBy && transition.createdBy.fullName + ' '}
        <FormattedMessage id="app.activity.createdAt" /> <FormattedDate value={transition.createdAt} />
        &nbsp;
        <FormattedTime value={transition.createdAt} />
    </span>
);

const getLink = (documentType: DocumentType, documentName: string, documentId: number, documentNumber?: number) => {
    let documentNumberLabel = <span>{documentNumber || documentId}</span>;

    if ([DocumentType.Credit, DocumentType.Invoice].includes(documentType)) {
        if (!documentNumber) {
            documentNumberLabel = (
                <FormattedMessage
                    id={`app.invoices.detail.${documentType === DocumentType.Credit ? 'creditDraft' : 'invoiceDraft'}`}
                />
            );
        }
    }

    return (
        <div>
            {/* A bare router link kept the browser's blue and purple. */}
            <Link component={NavLink} to={`/${getUrlSegment(documentType)}/${documentId}`} underline="hover">
                <FormattedMessage
                    id={'app.activity.transition.open'}
                    values={{ documentName, documentNumber: documentNumberLabel }}
                />
            </Link>
        </div>
    );
};

export const createTransitionTimelineItem = (
    transition: TransitionActivity,
    context: TransitionTimelineContext
): PoolTimelineItem => {
    let documentType = geDocumentTypeFromString(transition.toType);
    let isSourceDocument = false;

    if (context.sourceDocumentType === documentType && transition.toId === context.sourceDocumentId) {
        documentType = geDocumentTypeFromString(transition.fromType);
        isSourceDocument = true;
    }

    const documentName = context.intl.formatMessage({ id: getDocumentTranslationId(documentType) });
    const icon = isSourceDocument ? 'icon-check_circle' : 'icon-play_circle_filled';
    const headerId = isSourceDocument ? 'app.activity.transition.createdSource' : 'app.activity.transition.created';
    const linkDocumentId = isSourceDocument ? transition.fromId : transition.toId;
    const linkDocumentNumber = isSourceDocument ? transition.fromNumber : transition.toNumber;

    return {
        id: transition.id,
        key: `transition-${transition.id}`,
        timestamp: transition.createdAt.toISOString(),
        variant: isSourceDocument ? 'ok' : 'default',
        icon,
        header: <FormattedMessage id={headerId} values={{ documentName }} />,
        subheader: getSubHeader(transition),
        actor: transition.createdBy?.fullName,
        footer: undefined,
        content: getLink(documentType, documentName, linkDocumentId, linkDocumentNumber),
        commentsDisabled: true,
        comments: [],
        changes: [],
    };
};

export const createTransitionTimelineItems = (
    transitions: TransitionActivity[],
    context: TransitionTimelineContext
): PoolTimelineItem[] => transitions.map(transition => createTransitionTimelineItem(transition, context));
